import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0 0 1.5rem 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

interface BusinessRow {
  id: string;
  name: string;
  description?: string | null;
  website?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  approved?: boolean | null;
}

const normalizeWebsite = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getBusinessStatus = (approved?: boolean | null) => {
  if (approved === true) return 'Approved';
  if (approved === false) return 'Pending';
  return 'Pending';
};

const BusinessesPage: React.FC = () => {
  const {
    data: businesses,
    isLoading,
    error,
  } = useQuery<BusinessRow[]>({
    queryKey: ['businesses', 'connect-businesses-page'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('businesses')
        .select('id, name, description, website, address, phone, email, approved, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as BusinessRow[];
    },
  });

  return (
    <Page>
      <HeaderTitle>Businesses</HeaderTitle>
      <TableCard>
        <TableWrap>
          <Table>
            <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Contact</th>
              <th>Website</th>
              <th>Address</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6}>Loading businesses…</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6}>Error loading businesses.</td>
              </tr>
            ) : businesses?.length ? (
              businesses.map((business) => {
                const website = normalizeWebsite(business.website);
                return (
                  <tr key={business.id}>
                    <td>{business.name}</td>
                    <td>{business.description?.trim() || '-'}</td>
                    <td>
                      {business.phone || business.email
                        ? [business.phone, business.email].filter(Boolean).join(' · ')
                        : '-'}
                    </td>
                    <td>
                      {website ? (
                        <a href={website} target="_blank" rel="noreferrer">
                          {business.website}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{business.address?.trim() || '-'}</td>
                    <td>{getBusinessStatus(business.approved)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6}>No businesses found.</td>
              </tr>
            )}
          </tbody>
          </Table>
        </TableWrap>
      </TableCard>
    </Page>
  );
};

export default BusinessesPage;
